import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Search, Bell, Plus, LogOut, Menu, User as UserIcon,
  Ticket, Users as UsersIcon, Car, X,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const NOTIF_DOT = {
  booking: "bg-blue-500", payment: "bg-amber-500", maintenance: "bg-rose-500",
  cash: "bg-violet-500", system: "bg-emerald-500",
};
const RESULT_ICON = { customer: UsersIcon, booking: Ticket, vehicle: Car };

export default function Header({ onMenu }) {
  const { user, orgId, role, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/api/notifications", { params: { organization_id: orgId } })
      .then((r) => setNotifs(r.data)).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.get("/api/search", { params: { q, organization_id: orgId } })
        .then((r) => { setResults(r.data.results); setSearchOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [q, orgId]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const soon = (label) => toast.info(`${label} — module disponible prochainement`);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md sm:px-6">
      <button onClick={onMenu} data-testid="mobile-menu-button" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden">
        <Menu className="h-5 w-5" />
      </button>

      {/* Global search */}
      <div ref={boxRef} className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 2 && setSearchOpen(true)}
          data-testid="global-search-input"
          placeholder="Rechercher client, réservation, véhicule..."
          className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        )}
        {searchOpen && q.length >= 2 && (
          <div className="absolute left-0 right-0 top-12 z-40 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="search-results">
            {results.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">Aucun résultat</p>
            ) : (
              results.map((r) => {
                const Icon = RESULT_ICON[r.type] || Search;
                return (
                  <button key={`${r.type}-${r.id}`} onClick={() => { setSearchOpen(false); soon("Détail"); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50">
                    <Icon className="h-4 w-4 text-slate-400" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{r.label}</p>
                      {r.sub && <p className="truncate text-xs text-slate-400">{r.sub}</p>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {/* Quick actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="quick-actions-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nouveau</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate("/reservations?new=1")} data-testid="quick-action-new-reservation"><Ticket className="mr-2 h-4 w-4" /> Nouvelle réservation</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/depenses?new=1")} data-testid="quick-action-new-expense"><Plus className="mr-2 h-4 w-4" /> Ajouter une dépense</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/clients")} data-testid="quick-action-new-client"><UsersIcon className="mr-2 h-4 w-4" /> Voir les clients</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="notifications-button" className="relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100">
              <Bell className="h-5 w-5" />
              {notifs.unread > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white" data-testid="notifications-badge">
                  {notifs.unread}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="border-b border-slate-100 px-4 py-3"><p className="text-sm font-semibold text-slate-900">Notifications</p></div>
            <div className="max-h-96 overflow-y-auto">
              {notifs.notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification</p>
              ) : notifs.notifications.map((n) => (
                <div key={n.id} className="flex gap-3 border-b border-slate-50 px-4 py-3 last:border-0">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${NOTIF_DOT[n.type] || "bg-slate-400"}`} />
                  <div><p className="text-sm font-medium text-slate-800">{n.title}</p><p className="text-xs text-slate-500">{n.body}</p></div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-button" className="flex items-center gap-2 rounded-lg p-1 pr-2 transition-colors hover:bg-slate-100">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-white">{(user?.name || "?").charAt(0)}</div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
              <p className="text-xs font-normal text-slate-500">{user?.email}</p>
              {role && <span className="mt-1 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">{role}</span>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="profile-settings-item"><UserIcon className="mr-2 h-4 w-4" /> Mon profil</DropdownMenuItem>
            <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button" className="text-rose-600 focus:text-rose-600">
              <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
